import { useEffect, useState, useRef } from 'react';

const STORAGE_KEY = 'checkout_expiry_ts';

function CountdownTimer({ initialSeconds = 900, onExpired }) {
  const [timeLeft, setTimeLeft] = useState(0);
  const hasAlerted = useRef(false);

  useEffect(() => {
    let expiry = sessionStorage.getItem(STORAGE_KEY);

    if (!expiry) {
      expiry = Date.now() + initialSeconds * 1000;
      sessionStorage.setItem(STORAGE_KEY, expiry);
    } else {
      expiry = Number(expiry);
    }

    const tick = () => {
      const remaining = Math.max(0, Math.floor((expiry - Date.now()) / 1000));
      setTimeLeft(remaining);

      if (remaining <= 0) {
        sessionStorage.removeItem(STORAGE_KEY);
        if (onExpired) {
          onExpired();
        } else if (!hasAlerted.current) {
          hasAlerted.current = true;
          setTimeout(() => {
            alert('Payment expired. Please select your seat again.');
            window.location.href = '/';
          }, 1000);
        }
      }
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [initialSeconds, onExpired]);

  const hrs = Math.floor(timeLeft / 3600)
    .toString()
    .padStart(2, '0');
  const mins = Math.floor((timeLeft % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const secs = (timeLeft % 60).toString().padStart(2, '0');

  const isExpired = timeLeft <= 0;
  const isWarning = timeLeft <= 300 && !isExpired; // 5 minutes or less

  return (
    <div
      className={`border rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 ${
        isExpired
          ? 'bg-red-600/5 dark:bg-red-600/10 border-red-600/20'
          : isWarning
            ? 'bg-orange-600/5 dark:bg-orange-600/10 border-orange-600/20'
            : 'bg-[#135bec]/5 dark:bg-[#135bec]/10 border-[#135bec]/20'
      }`}
    >
      <div className='flex items-center gap-4'>
        <div
          className={`text-white p-2 rounded-lg flex items-center ${
            isExpired
              ? 'bg-red-600'
              : isWarning
                ? 'bg-orange-600'
                : 'bg-[#135bec]'
          }`}
        >
          <span className='material-symbols-outlined'>timer</span>
        </div>
        <div>
          <p className='font-bold text-slate-900 dark:text-slate-100 m-0'>
            {isExpired ? 'Booking expired' : 'Complete your booking soon'}
          </p>
          <p className='text-sm text-slate-500 dark:text-slate-300 m-0 font-normal'>
            {isExpired
              ? 'Your seats are no longer placed on hold'
              : 'Your seats are held for a limited time'}
          </p>
        </div>
      </div>
      <div className='flex gap-3'>
        <div className='flex flex-col items-center'>
          <div
            className={`bg-white dark:bg-slate-800 border w-12 h-12 flex items-center justify-center rounded-lg shadow-sm ${
              isExpired
                ? 'border-red-600/20'
                : isWarning
                  ? 'border-orange-600/20'
                  : 'border-[#135bec]/20'
            }`}
          >
            <span
              className={`text-xl font-bold ${
                isExpired
                  ? 'text-red-600'
                  : isWarning
                    ? 'text-orange-600'
                    : 'text-[#135bec]'
              }`}
            >
              {hrs}
            </span>
          </div>
          <span
            className={`text-[10px] uppercase font-bold mt-1 tracking-wider ${
              isExpired
                ? 'text-red-500'
                : isWarning
                  ? 'text-orange-500'
                  : 'text-slate-400'
            }`}
          >
            Hrs
          </span>
        </div>
        <div
          className={`text-2xl font-bold self-center pb-4 ${
            isExpired
              ? 'text-red-600'
              : isWarning
                ? 'text-orange-600'
                : 'text-[#135bec]'
          }`}
        >
          :
        </div>
        <div className='flex flex-col items-center'>
          <div
            className={`bg-white dark:bg-slate-800 border w-12 h-12 flex items-center justify-center rounded-lg shadow-sm ${
              isExpired
                ? 'border-red-600/20'
                : isWarning
                  ? 'border-orange-600/20'
                  : 'border-[#135bec]/20'
            }`}
          >
            <span
              className={`text-xl font-bold ${
                isExpired
                  ? 'text-red-600'
                  : isWarning
                    ? 'text-orange-600'
                    : 'text-[#135bec]'
              }`}
            >
              {mins}
            </span>
          </div>
          <span
            className={`text-[10px] uppercase font-bold mt-1 tracking-wider ${
              isExpired
                ? 'text-red-500'
                : isWarning
                  ? 'text-orange-500'
                  : 'text-slate-400'
            }`}
          >
            Min
          </span>
        </div>
        <div
          className={`text-2xl font-bold self-center pb-4 ${
            isExpired
              ? 'text-red-600'
              : isWarning
                ? 'text-orange-600'
                : 'text-[#135bec]'
          }`}
        >
          :
        </div>
        <div className='flex flex-col items-center'>
          <div
            className={`bg-white dark:bg-slate-800 border w-12 h-12 flex items-center justify-center rounded-lg shadow-sm ${
              isExpired
                ? 'border-red-600/20'
                : isWarning
                  ? 'border-orange-600/20'
                  : 'border-[#135bec]/20'
            }`}
          >
            <span
              className={`text-xl font-bold ${
                isExpired
                  ? 'text-red-600'
                  : isWarning
                    ? 'text-orange-600'
                    : 'text-[#135bec]'
              }`}
            >
              {secs}
            </span>
          </div>
          <span
            className={`text-[10px] uppercase font-bold mt-1 tracking-wider ${
              isExpired
                ? 'text-red-500'
                : isWarning
                  ? 'text-orange-500'
                  : 'text-slate-400'
            }`}
          >
            Sec
          </span>
        </div>
      </div>
    </div>
  );
}

export default CountdownTimer;
